<!-- appendTo body: this tab renders inside the full-viewport Project Details modal, so a
     dialog left in place is created inside that stacking context and can open behind the
     parent's mask. The add/edit dialog below does the same for the same reason. -->
<p-confirmDialog appendTo="body" />

<div class="plan-page-container">
  <div class="plan-page-header">
    <div class="pt-title-block">
      <!-- Suppressed when embedded: the Work Log page's project group already names it. -->
      <h3 class="m-0" *ngIf="!embedded">Tasks</h3>
      <!-- Progress of the whole backlog, so the headline answers "are we nearly there"
           before any row is read. -->
      <div class="pt-summary" *ngIf="tasks.length">
        <span class="pt-summary-bar">
          <span class="pt-summary-fill" [style.width.%]="donePercent"></span>
        </span>
        <span class="pt-summary-text">
          <strong>{{ doneCount }}</strong> of {{ tasks.length }} done
          <span class="pt-summary-pct">({{ donePercent }}%)</span>
        </span>
        <span class="pt-flag pt-flag--blocked" *ngIf="blockedCount"
              pTooltip="Blocked tasks" tooltipPosition="top">
          <i class="pi pi-ban"></i>{{ blockedCount }} blocked
        </span>
        <span class="pt-flag pt-flag--overdue" *ngIf="overdueCount"
              pTooltip="Past their start date and not started" tooltipPosition="top">
          <i class="pi pi-exclamation-triangle"></i>{{ overdueCount }} late to start
        </span>
      </div>
    </div>
    <p-button *ngIf="canManage" label="Add" icon="pi pi-plus" (onClick)="showAddDialog()"
              severity="info" [rounded]="true" />
  </div>

  <!-- Filters appear once there is enough to sift. Below that the list is short enough to
       read whole, and a row of empty dropdowns is just noise. -->
  <div class="pt-filters" *ngIf="tasks.length > 5">
    <!-- One-click states, so the common filter needs no dropdown. -->
    <div class="pt-chips">
      <button type="button" class="pt-chip" *ngFor="let chip of quickFilters"
              [ngClass]="'pt-chip--' + chip.severity"
              [class.pt-chip--active]="isProgressFilterActive(chip.value)"
              [attr.aria-pressed]="isProgressFilterActive(chip.value)"
              (click)="toggleProgressFilter(chip.value)">
        {{ chip.label }}<span class="pt-chip-count">{{ chip.count }}</span>
      </button>
    </div>

    <div class="pt-filter-controls">
      <span class="pt-search-wrap">
        <i class="pi pi-search" aria-hidden="true"></i>
        <input type="text" pInputText class="pt-search" [(ngModel)]="searchText"
               placeholder="Search tasks…" aria-label="Search tasks" />
      </span>

      <p-multiSelect [options]="sprintOptions" [(ngModel)]="selectedSprints"
                     optionLabel="label" optionValue="value" placeholder="Sprint"
                     [maxSelectedLabels]="1" selectedItemsLabel="{0} sprints"
                     [showToggleAll]="true"
                     panelStyleClass="filter-dropdown-panel" appendTo="body"
                     styleClass="pt-filter-select" />

      <p-multiSelect [options]="ownerOptions" [(ngModel)]="selectedOwners"
                     optionLabel="label" optionValue="value" placeholder="Owner"
                     [maxSelectedLabels]="1" selectedItemsLabel="{0} owners"
                     [showToggleAll]="true" [filter]="true" filterPlaceholder="Search…"
                     panelStyleClass="filter-dropdown-panel" appendTo="body"
                     styleClass="pt-filter-select" />

      <button type="button" class="pt-icon-btn" *ngIf="hasActiveFilters" (click)="clearFilters()"
              pTooltip="Clear filters" tooltipPosition="top" aria-label="Clear filters">
        <i class="pi pi-filter-slash"></i>
      </button>
    </div>
  </div>

  <div *ngIf="loading" class="loading-container loading-container--centered">
    <i class="pi pi-spin pi-spinner loading-spinner-icon"></i>
    <p>Loading tasks…</p>
  </div>

  <ng-container *ngIf="!loading">
    <!-- Empty: one table with headers showing, as the sibling plan tabs do. Seeing the
         columns tells you what a task will hold before you add one. -->
    <p-table *ngIf="sprintGroups.length === 0" [value]="[]"
             [tableStyle]="{ 'min-width': '100%' }"
             styleClass="p-datatable no-column-lines compact-striped-table pt-table">
      <ng-template pTemplate="header">
        <tr>
          <th class="pt-col-num">Task No</th>
          <th>Task Name</th>
          <th class="pt-col-date">Created</th>
          <th class="pt-col-date">Start Date</th>
          <th class="pt-col-date">Target Date</th>
          <th class="pt-col-owner">Owner</th>
          <th class="pt-col-progress">Progress</th>
          <th class="pt-col-effort">Effort</th>
          <th class="pt-col-date">Completed</th>
          <th class="th-actions" *ngIf="canManage">Actions</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr class="table-empty-row">
          <td [attr.colspan]="canManage ? 10 : 9" class="table-empty-cell">
            <div class="empty-state">
              <div class="empty-icon"><i class="pi pi-inbox"></i></div>
              <p class="empty-message">
                {{ hasActiveFilters ? 'No tasks match these filters' : 'No tasks found' }}
              </p>
              <p class="empty-description">
                {{ hasActiveFilters
                    ? 'Try a different search, or clear the filters to see the whole backlog'
                    : 'Click "Add" to create a new task' }}
              </p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>

    <!-- One table per sprint, so the sprint heading carries its own count. -->
    <div class="pt-sprint" *ngFor="let group of sprintGroups">
      <div class="pt-sprint-head" [class.pt-sprint-head--complete]="group.doneCount === group.tasks.length">
        <span class="pt-sprint-name">
          <i class="pi pi-check-circle" *ngIf="group.doneCount === group.tasks.length"
             aria-hidden="true"></i>
          {{ group.sprint }}
        </span>
        <span class="pt-sprint-progress">
          <span class="pt-sprint-bar">
            <span class="pt-sprint-fill"
                  [style.width.%]="(group.doneCount / group.tasks.length) * 100"></span>
          </span>
          <span class="pt-sprint-count">{{ group.doneCount }} / {{ group.tasks.length }}</span>
        </span>
      </div>

      <p-table [value]="group.tasks" [tableStyle]="{ 'min-width': '100%' }"
               styleClass="p-datatable no-column-lines compact-striped-table pt-table">
        <ng-template pTemplate="header">
          <tr>
            <th class="pt-col-num">Task No</th>
            <th>Task Name</th>
            <th class="pt-col-date">Created</th>
            <th class="pt-col-date">Start Date</th>
            <th class="pt-col-date">Target Date</th>
            <th class="pt-col-owner">Owner</th>
            <th class="pt-col-progress">Progress</th>
            <th class="pt-col-effort">Effort</th>
            <th class="pt-col-date">Completed</th>
            <th class="th-actions" *ngIf="canManage">Actions</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-task>
          <!-- Edit is the pencil, not the whole row. A row click competed with the action
               buttons inside it, and the sibling tabs do not make rows clickable either. -->
          <tr [class.pt-row-done]="task.progress === 'Done'"
              [class.pt-row-blocked]="task.progress === 'Blocked'">
            <td class="pt-col-num">{{ task.taskNumber || '—' }}</td>
            <td>
              <div class="pt-name">{{ task.name }}</div>
              <div class="pt-desc" *ngIf="task.description"
                   [pTooltip]="task.description" tooltipPosition="top">{{ task.description }}</div>
            </td>
            <td class="pt-col-date">{{ formatDate(task.createdDate) }}</td>
            <td class="pt-col-date" [class.pt-late]="isOverdue(task)"
                [pTooltip]="isOverdue(task) ? 'Start date has passed and the task is still To Do' : ''"
                tooltipPosition="top">
              <i class="pi pi-exclamation-triangle" *ngIf="isOverdue(task)" aria-hidden="true"></i>
              {{ formatDate(task.startDate) }}
            </td>
            <!-- Past its target and not done: the one date on the row worth interrupting for. -->
            <td class="pt-col-date" [class.pt-late]="isPastTarget(task)"
                [pTooltip]="isPastTarget(task) ? 'Past its target date and not finished' : ''"
                tooltipPosition="top">
              <i class="pi pi-exclamation-circle" *ngIf="isPastTarget(task)" aria-hidden="true"></i>
              {{ formatDate(task.targetDate) }}
            </td>
            <td class="pt-col-owner">
              <span *ngIf="task.ownerName">{{ task.ownerName }}</span>
              <!-- Unassigned work is a gap worth seeing, not a blank cell. -->
              <span class="pt-unassigned" *ngIf="!task.ownerName">Unassigned</span>
            </td>
            <td class="pt-col-progress">
              <div class="pt-progress">
                <p-tag [value]="task.progress" [severity]="progressSeverity(task.progress)" />
                <span class="pt-progress-bar">
                  <span class="pt-progress-fill"
                        [style.width.%]="task.progressPercentage || 0"></span>
                </span>
                <span class="pt-progress-pct">{{ task.progressPercentage || 0 }}%</span>
              </div>
            </td>
            <!-- Total plus a quick way to add to it, so logging hours on an existing task
                 does not mean opening the whole form. The tooltip lists the entries behind
                 the total, which is the thing a single number cannot show. -->
            <td class="pt-col-effort">
              <div class="pt-effort">
                <!-- The total is the way in to the log, not just a figure: clicking it opens
                     the entries behind it. A hover-only + was the only route before, which
                     left the history effectively undiscoverable. Server-formatted via
                     TimesheetHours, so it reads as the Work Log would render it. -->
                <button type="button" class="pt-effort-total"
                        [class.pt-effort-total--zero]="!task.totalEffortHours"
                        [disabled]="!canManage"
                        (click)="openEffortDialog(task)"
                        [pTooltip]="canManage ? effortBreakdown(task) + '\n\nClick to view or add entries'
                                              : effortBreakdown(task)"
                        tooltipPosition="top" [tooltipOptions]="{ showDelay: 300 }">
                  {{ task.totalEffortDisplay || '0h 0min' }}
                </button>
                <button type="button" class="pt-effort-add" *ngIf="canManage"
                        (click)="openEffortDialog(task)"
                        pTooltip="Log more hours" tooltipPosition="top"
                        aria-label="Log more hours">
                  <i class="pi pi-plus" aria-hidden="true"></i>
                </button>
              </div>
            </td>
            <td class="pt-col-date">{{ formatDate(task.completedDate) }}</td>
            <!-- Native <button> with (click), not <p-button> with (onClick). The p-button
                 version rendered correctly but its output never fired from inside the
                 table body template — no error, no request, nothing. A plain DOM click
                 cannot fail that way, and the chip filters above already use it. The
                 shared table-cell-actions / table-actions-stack classes keep the
                 placement identical to the sibling tabs. -->
            <td class="table-cell-actions" *ngIf="canManage">
              <div class="table-actions-stack">
                <button type="button" class="pt-action-btn pt-action-btn--edit"
                        (click)="showEditDialog(task)"
                        pTooltip="Edit" tooltipPosition="top" aria-label="Edit task">
                  <i class="pi pi-pencil" aria-hidden="true"></i>
                </button>
                <!-- Hidden for the Work Log copy: developers add, edit and log effort, but
                     deleting a task stays a manager action in the Projects module. -->
                <button type="button" class="pt-action-btn pt-action-btn--delete"
                        *ngIf="!readonlyDelete"
                        (click)="confirmDelete(task)"
                        pTooltip="Delete" tooltipPosition="top" aria-label="Delete task">
                  <i class="pi pi-trash" aria-hidden="true"></i>
                </button>
              </div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </ng-container>
</div>

<!-- Quick effort log. Separate from the task form so hours can be added without opening
     and re-saving the whole task, and so the existing entries are visible while doing it. -->
<p-dialog
  [visible]="displayEffortDialog"
  (visibleChange)="displayEffortDialog = $event"
  header="Log effort"
  [modal]="true"
  styleClass="dialog-md"
  appendTo="body"
  [dismissableMask]="false"
  [closable]="!effortInFlight">

  <div class="form-dialog-form form-dialog-compact" *ngIf="effortTask">
    <div class="form-dialog-container">
      <!-- Which task, and what it stands at — the two facts needed before typing a number. -->
      <div class="pt-effort-head">
        <div class="pt-effort-head-task">
          <span class="pt-effort-head-no">{{ effortTask.taskNumber || '—' }}</span>
          <span class="pt-effort-head-name">{{ effortTask.name }}</span>
        </div>
        <div class="pt-effort-head-total">
          <span class="pt-effort-head-value">{{ effortTask.totalEffortDisplay || '0h 0min' }}</span>
          <span class="pt-effort-head-label">logged so far</span>
        </div>
      </div>

      <div class="form-row two-columns">
        <div class="field">
          <label>Hours <span class="required-star">*</span></label>
          <p-inputNumber [(ngModel)]="effortHours" [min]="0" [max]="999" [maxFractionDigits]="2"
                         placeholder="e.g. 2.30" styleClass="w-full" />
          <small class="field-hint">2.30 = 2h 30m, as in Work Log</small>
          <small class="field-error" *ngIf="effortMinutesInvalid">
            Minutes must be 00–59 — enter 2.45 for 2h 45m, not 2.75.
          </small>
        </div>
        <div class="field">
          <label>Date</label>
          <p-datepicker [(ngModel)]="effortDate" [showIcon]="true" dateFormat="dd M yy"
                        placeholder="Today" appendTo="body" [showOnFocus]="false"
                        [autoZIndex]="true" styleClass="w-full" />
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Note</label>
          <input pInputText [(ngModel)]="effortNotes" placeholder="What the hours went on"
                 maxlength="500" />
        </div>
      </div>

      <!-- The entries behind the total. Shown here because this is where a wrong one gets
           removed — correcting by logging a negative would misreport time spent. -->
      <div class="pt-effort-log" *ngIf="effortTask.effortEntries?.length">
        <h4 class="pt-effort-log-title">Logged so far</h4>
        <div class="pt-effort-row" *ngFor="let entry of effortTask.effortEntries">
          <span class="pt-effort-row-hours">{{ entry.hoursDisplay || '0h 0min' }}</span>
          <span class="pt-effort-row-date">{{ formatDate(entry.effortDate) }}</span>
          <span class="pt-effort-row-who">{{ entry.employeeName || 'Unattributed' }}</span>
          <span class="pt-effort-row-note" [pTooltip]="entry.notes || ''"
                tooltipPosition="top">{{ entry.notes || '' }}</span>
          <button type="button" class="pt-effort-row-del" *ngIf="canManage"
                  (click)="confirmDeleteEffort(entry)"
                  pTooltip="Remove this entry" tooltipPosition="top"
                  aria-label="Remove this entry">
            <i class="pi pi-times" aria-hidden="true"></i>
          </button>
        </div>

        <!-- Closes the loop: the entries above visibly add up to the figure in the column,
             so the total is checkable rather than asserted. -->
        <div class="pt-effort-row pt-effort-row--total">
          <span class="pt-effort-row-hours">{{ effortTask.totalEffortDisplay || '0h 0min' }}</span>
          <span class="pt-effort-row-date">Total</span>
        </div>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" icon="pi pi-times" (onClick)="displayEffortDialog = false"
              [text]="true" severity="secondary" [disabled]="effortInFlight" />
    <p-button label="Add hours" icon="pi pi-check" (onClick)="saveEffort()"
              severity="primary" [loading]="effortInFlight"
              [disabled]="!effortHours || effortHours <= 0 || effortMinutesInvalid" />
  </ng-template>
</p-dialog>

<!-- Add / Edit dialog. appendTo body so it stacks above the full-viewport Project Details
     parent modal, as the sibling plan dialogs do. -->
<p-dialog
  [visible]="displayDialog"
  (visibleChange)="displayDialog = $event"
  [header]="isEditMode ? 'Edit Task' : 'Add Task'"
  [modal]="true"
  styleClass="dialog-lg"
  appendTo="body"
  [dismissableMask]="false"
  [closable]="!saveInFlight">

  <div class="form-dialog-form form-dialog-compact">
    <div class="form-dialog-container">
      <div class="form-row two-columns">
        <div class="field">
          <label>Sprint</label>
          <input pInputText [(ngModel)]="form.sprintNumber" placeholder="e.g. S1" maxlength="50" />
        </div>
        <div class="field">
          <label>Task No</label>
          <input pInputText [(ngModel)]="form.taskNumber"
                 [placeholder]="isEditMode ? 'e.g. T-001' : (nextTaskNumber || 'e.g. T-001')"
                 maxlength="50" />
          <!-- Blank means "number it for me", so the placeholder shows what will be used. -->
          <small class="field-hint" *ngIf="!isEditMode && nextTaskNumber">
            Leave blank to use {{ nextTaskNumber }}
          </small>
        </div>
      </div>

      <div class="form-row">
        <div class="field" [class.field-invalid]="fieldTouched['name'] && !form.name.trim()">
          <label>Task Name <span class="required-star">*</span></label>
          <input pInputText [(ngModel)]="form.name" placeholder="What needs doing"
                 maxlength="300" (blur)="fieldTouched['name'] = true" />
          <small class="field-error" *ngIf="fieldTouched['name'] && !form.name.trim()">
            Task name is required
          </small>
        </div>
      </div>

      <div class="form-row two-columns">
        <div class="field">
          <label>Owner</label>
          <p-select [(ngModel)]="form.ownerId" [options]="employeeOptions"
                    optionLabel="label" optionValue="value" placeholder="Unassigned"
                    [showClear]="true" [filter]="true" filterPlaceholder="Search employee…"
                    panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
        <div class="field">
          <label>Start date</label>
          <p-datepicker [(ngModel)]="startDate" [showIcon]="true" dateFormat="dd M yy"
                        placeholder="Not scheduled" [showClear]="true"
                        appendTo="body" [showOnFocus]="false" [autoZIndex]="true"
                        (ngModelChange)="onStartDateChange()"
                        styleClass="w-full" />
        </div>
      </div>

      <!-- The two planned-vs-actual dates sit together: target is the plan, completed is
           what happened, and reading them side by side is how a task shows as late. -->
      <div class="form-row two-columns">
        <div class="field">
          <label>Target date</label>
          <!-- minDate binds the field itself, not an expression, so its reference stays
               stable between change-detection cycles. -->
          <p-datepicker [(ngModel)]="targetDate" [showIcon]="true" dateFormat="dd M yy"
                        placeholder="No due date" [showClear]="true"
                        [minDate]="startDate!"
                        appendTo="body" [showOnFocus]="false" [autoZIndex]="true"
                        styleClass="w-full" />
        </div>
        <div class="field">
          <label>Completed date</label>
          <p-datepicker [(ngModel)]="completedDate" [showIcon]="true" dateFormat="dd M yy"
                        placeholder="Set when progress is Done" [showClear]="true"
                        [disabled]="form.progress !== 'Done'"
                        appendTo="body" [showOnFocus]="false" [autoZIndex]="true"
                        styleClass="w-full" />
          <small class="field-hint" *ngIf="form.progress !== 'Done'">
            Available once progress is Done.
          </small>
        </div>
      </div>

      <div class="form-row two-columns">
        <div class="field">
          <label>Progress</label>
          <p-select [(ngModel)]="form.progress" [options]="progressOptions"
                    optionLabel="label" optionValue="value"
                    (ngModelChange)="onProgressChange()"
                    panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
        <div class="field">
          <label>Progress %</label>
          <!-- Quarter steps rather than a spinner. 100% appears only when the state is
               Done, because the server will not store it on anything else. -->
          <p-select [(ngModel)]="form.progressPercentage" [options]="percentOptions"
                    optionLabel="label" optionValue="value"
                    panelStyleClass="filter-dropdown-panel" appendTo="body" styleClass="w-full" />
        </div>
      </div>

      <!-- Effort is cumulative: whatever is entered here is ADDED to the task's log, never
           substituted for it. The field starts empty on every open for exactly that reason —
           a prefilled total would invite someone to "correct" it and silently double-log. -->
      <div class="form-row two-columns">
        <div class="field">
          <!-- The running total sits on the label, where it reads as context for the field
               rather than as another sentence beneath it. "3h 0min logged" is also the
               link to the entries behind that figure. -->
          <label class="pt-effort-label">
            <span>Add effort (hours)</span>
            <button type="button" class="pt-link-btn" *ngIf="isEditMode"
                    (click)="viewEffortHistory()"
                    pTooltip="View or remove logged entries" tooltipPosition="top">
              {{ existingEffortDisplay }} logged
            </button>
          </label>
          <p-inputNumber [(ngModel)]="effortHours" [min]="0" [max]="999" [maxFractionDigits]="2"
                         placeholder="e.g. 2.30" styleClass="w-full" />
          <!-- Same convention as the Work Log: 2.30 is 2h 30m, not two and a half hours. -->
          <!-- One line, not three. The format note is the only thing that has to be said
               here; the running total belongs on the label beside it, where it is read
               rather than explained. -->
          <small class="field-hint">2.30 = 2h 30m, as in Work Log</small>
          <small class="field-error" *ngIf="effortMinutesInvalid">
            Minutes must be 00–59 — enter 2.45 for 2h 45m, not 2.75.
          </small>
        </div>
        <div class="field">
          <label>Effort note</label>
          <input pInputText [(ngModel)]="effortNotes" placeholder="What the hours went on"
                 maxlength="500" [disabled]="!effortHours" />
        </div>
      </div>

      <div class="form-row">
        <div class="field">
          <label>Description</label>
          <textarea pInputText [(ngModel)]="form.description" rows="4"
                    placeholder="What this covers, acceptance notes…"></textarea>
        </div>
      </div>
    </div>

    <!-- In-flow footer, matching the sibling plan dialogs: a portaled p-dialog-footer lands
         off-screen when stacked on the full-viewport Project Details modal. -->
    <div class="form-dialog-inline-footer">
      <p-button label="Cancel" icon="pi pi-times" (onClick)="displayDialog = false"
                severity="secondary" [outlined]="true" [disabled]="saveInFlight" />
      <p-button label="Save" icon="pi pi-check" (onClick)="save()" severity="primary"
                [loading]="saveInFlight" [disabled]="saveInFlight || !form.name.trim()" />
    </div>
  </div>
</p-dialog>
